import React, { createRef } from "react";
import { string, node, shape } from "prop-types";
import { Helmet } from "react-helmet";
import { Link } from "react-router";
import { Menu, Sticky } from "semantic-ui-react";
import ConfirmationDialogContainer from "shared/components/dialogs/ConfirmationDialogContainer";
import PageMenu from "shared/components/pageLayout/PageMenu";
import PageNavigation from "shared/components/pageLayout/PageNavigation";
import { I18nShape } from "shared/shapes/i18n.shape";
import { UserShape } from "shared/shapes/user.shape";
import PageFooter from "../navigation/PageFooter";
import GrowlMessage from "../../../shared/components/growlMessage/GrowlMessage";

import "shared/components/pageLayout/pageLayout.scss";
import "shared/components/pageLayout/pageMenu.scss";

class PageLayout extends React.Component {
  contextRef = createRef();

  constructor(props) {
    super(props);
    this.state = {
      sidebarIsVisible: false
    };
    this.handleToggleSidebar = this.handleToggleSidebar.bind(this);
  }

  handleToggleSidebar() {
    const { sidebarIsVisible } = this.state;
    this.setState({ sidebarIsVisible: !sidebarIsVisible });
  }

  render() {
    const {
      accountStyles,
      children,
      i18n,
      layoutScope,
      only3D,
      pageName,
      projectBannerImageUrl,
      projectBranding,
      projectId,
      projectLogoUrl,
      projectStyles,
      unitId,
      unitPath,
      user
    } = this.props;
    const { sidebarIsVisible } = this.state;

    const pageTitle = [i18n[pageName], i18n["meta.app.name"]].join(" - ");

    const root = document.documentElement;

    if (projectBannerImageUrl?.length) {
      root.style.setProperty(
        "--project-banner-image-url",
        `url(${projectBannerImageUrl})`
      );
    }

    if (projectLogoUrl?.length) {
      root.style.setProperty("--project-logo-url", `url(${projectLogoUrl})`);
    }

    if (projectBranding.branding_color?.length) {
      root.style.setProperty(
        "--project-branding-color",
        projectBranding.branding_color
      );
    }

    if (projectBranding.branding_contrast_color?.length) {
      root.style.setProperty(
        "--project-branding-contrast-color",
        projectBranding.branding_contrast_color
      );
    }

    // ignore Wireframe on specific 3d Flag (was a quick n dirty hotfeature)
    if (only3D) {
      // we will have to change body-background, in ProjectStyles, add a class for this intention.
      document.body.classList.add("only3d");
      return (
        <div data-component="pageLayout">
          <style>
            {accountStyles}
            {projectStyles}
          </style>
          {children}
        </div>
      );
    }

    return (
      <div data-component="pageLayout">
        <div
          className="buyer-portal"
          data-mode="marketing"
          data-scope={layoutScope}
        >
          <style>
            {accountStyles}
            {projectStyles}
          </style>
          <Helmet title={pageTitle} />
          <PageNavigation
            projectId={projectId}
            unitId={unitId}
            onToggleSidebar={this.handleToggleSidebar}
            sidebarIsVisible={sidebarIsVisible}
            rootPath={unitPath}
          />
          <div
            className="banner"
            style={
              projectBannerImageUrl
                ? { backgroundImage: `url(${projectBannerImageUrl})` }
                : null
            }
          >
            <Link to={unitPath} onlyActiveOnIndex>
              <div
                style={{ backgroundImage: `url(${projectLogoUrl})` }}
                className="banner-image"
              />
            </Link>
          </div>
          <div data-component="pageBody" className="page">
            <Sticky
              context={this.contextRef}
              offset={70}
              styleElement={{ height: 0 }}
            >
              <GrowlMessage />
            </Sticky>
            <div ref={this.contextRef}>{children}</div>
          </div>
          {user && (
            <div
              data-component="sidebar"
              style={{ display: sidebarIsVisible ? "block" : "none" }}
            >
              <Menu
                vertical
                inverted
                size="large"
                style={{ height: "100%", width: "300px", marginTop: "60px" }}
              >
                <PageMenu
                  projectId={projectId}
                  unitId={unitId}
                  sidebarIsVisible={sidebarIsVisible}
                  onToggleSidebar={this.handleToggleSidebar}
                />
              </Menu>
            </div>
          )}

          <PageFooter />
          <ConfirmationDialogContainer
            module="buyer_portal"
            id="confirmation_dialog"
          />
        </div>
      </div>
    );
  }
}

PageLayout.defaultProps = {
  projectBranding: {
    branding_color: "",
    branding_contrast_color: "",
    warning_color: ""
  },
  user: undefined
};

PageLayout.propTypes = {
  accountStyles: string.isRequired,
  children: node.isRequired,
  i18n: I18nShape.isRequired,
  layoutScope: string.isRequired,
  only3D: string.isRequired,
  pageName: string.isRequired,
  projectBannerImageUrl: string.isRequired,
  projectBranding: shape({
    branding_color: string,
    branding_contrast_color: string,
    warning_color: string
  }),
  projectId: string.isRequired,
  projectLogoUrl: string.isRequired,
  projectStyles: string.isRequired,
  unitId: string.isRequired,
  unitPath: string.isRequired,
  user: UserShape
};

export default PageLayout;
